import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { saveNotificationPreferences } from '../../utilities/firebase';
import {
  generateGoogleCalendarUrl,
  generateOutlookUrl,
  generateIcsContent,
  downloadIcsFile,
  hasSelectedDays,
} from '../../utilities/calendarUtils';
import {
  DEFAULT_REMINDER_DAYS,
  DEFAULT_REMINDER_TIME,
  DAY_KEYS,
  getTimeOptions,
  buildNotificationPreferences,
} from '../../utilities/reminderUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { trackReminderSetupEvent } from '../../utilities/notificationPrompt';
import './ReminderSetupCard.css';

/**
 * Compact reminder setup card shown on the ObjectiveView while planner generates.
 * Lets users set up practice reminders (email + calendar) without leaving the page.
 */
function ReminderSetupCard({ source = 'objective_page', onSaved }) {
  const { t } = useTranslation();

  const nativeLanguageCode = localStorage.getItem('userLanguage') || 'en';
  const targetLanguageCode = localStorage.getItem('targetLanguage') || 'fr';

  const [reminderDays, setReminderDays] = useState(DEFAULT_REMINDER_DAYS);
  const [reminderTime, setReminderTime] = useState(DEFAULT_REMINDER_TIME);
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [calendarState, setCalendarState] = useState({
    addedGoogle: false,
    addedOutlook: false,
    downloadedIcs: false,
  });
  const [saved, setSaved] = useState(false);

  const timeOptions = getTimeOptions(nativeLanguageCode);

  const dayLabels = DAY_KEYS.map(key => ({
    key,
    label: t(`day_${key}`, key.charAt(0).toUpperCase() + key.slice(1)),
  }));

  const handleDayToggle = (index) => {
    const newDays = [...reminderDays];
    newDays[index] = !newDays[index];
    setReminderDays(newDays);
  };

  const handleSave = async () => {
    const preferences = buildNotificationPreferences(reminderDays, reminderTime, emailEnabled, calendarState);
    await saveNotificationPreferences(preferences);
    setSaved(true);
    trackReminderSetupEvent(preferences, { source });
    if (onSaved) {
      onSaved(preferences);
    }
  };

  const daysSelected = hasSelectedDays(reminderDays);

  const handleGoogleCalendarClick = () => {
    if (!daysSelected) return;
    const url = generateGoogleCalendarUrl(reminderTime, reminderDays, targetLanguageCode);
    window.open(url, '_blank', 'noopener,noreferrer');
    setCalendarState(prev => ({ ...prev, addedGoogle: true }));
  };

  const handleOutlookClick = () => {
    if (!daysSelected) return;
    const url = generateOutlookUrl(reminderTime, reminderDays, targetLanguageCode);
    window.open(url, '_blank', 'noopener,noreferrer');
    setCalendarState(prev => ({ ...prev, addedOutlook: true }));
  };

  const handleIcsDownload = () => {
    if (!daysSelected) return;
    const icsContent = generateIcsContent(reminderTime, reminderDays, targetLanguageCode);
    downloadIcsFile(icsContent);
    setCalendarState(prev => ({ ...prev, downloadedIcs: true }));
  };

  return (
    <div className="reminder-card">
      <h3 className="reminder-card-title">
        {saved
          ? t('reminders_saved', 'Reminders saved!')
          : t('reminder_card_title', 'While you wait, set up practice reminders')
        }
        {saved && <span className="reminder-title-check" aria-hidden="true">&#10003;</span>}
      </h3>

      {!saved && (
        <>
          <p className="reminder-card-subtitle">
            {t('reminder_card_subtitle', 'Regular short sessions are the key to progress.')}
          </p>

          {/* Day toggles */}
          <div className="reminder-days" role="group" aria-label={t('day_selection', 'Day selection')}>
            {dayLabels.map((day, index) => (
              <button
                key={day.key}
                type="button"
                className={`reminder-day-btn ${reminderDays[index] ? 'active' : ''}`}
                onClick={() => handleDayToggle(index)}
                aria-pressed={reminderDays[index]}
              >
                {day.label}
              </button>
            ))}
          </div>

          {/* Time + Email row */}
          <div className="reminder-options-row">
            <select
              className="reminder-time-select"
              value={reminderTime}
              onChange={(e) => setReminderTime(e.target.value)}
              aria-label={t('reminder_time_selection', 'Reminder time')}
            >
              {timeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <label className="reminder-email-label">
              <input
                type="checkbox"
                checked={emailEnabled}
                onChange={(e) => setEmailEnabled(e.target.checked)}
                className="reminder-email-checkbox"
              />
              <span>{t('onboarding_email_checkbox_label', 'Send me email reminders')}</span>
            </label>
          </div>

          {/* Calendar buttons */}
          <div className="reminder-calendar-row">
            <button
              type="button"
              className={`reminder-cal-btn ${calendarState.addedGoogle ? 'added' : ''}`}
              onClick={handleGoogleCalendarClick}
              disabled={!daysSelected}
            >
              <svg className="reminder-cal-icon" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path fill="currentColor" d="M19.5 3h-15A1.5 1.5 0 003 4.5v15A1.5 1.5 0 004.5 21h15a1.5 1.5 0 001.5-1.5v-15A1.5 1.5 0 0019.5 3zm-9 15H6V9h4.5v9zm7.5 0h-4.5V9H18v9z"/>
              </svg>
              {calendarState.addedGoogle ? t('onboarding_calendar_added', 'Added') + ' \u2713' : 'Google'}
            </button>
            <button
              type="button"
              className={`reminder-cal-btn ${calendarState.addedOutlook ? 'added' : ''}`}
              onClick={handleOutlookClick}
              disabled={!daysSelected}
            >
              <svg className="reminder-cal-icon" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
              </svg>
              {calendarState.addedOutlook ? t('onboarding_calendar_added', 'Added') + ' \u2713' : 'Outlook'}
            </button>
            <button
              type="button"
              className={`reminder-cal-btn ${calendarState.downloadedIcs ? 'added' : ''}`}
              onClick={handleIcsDownload}
              disabled={!daysSelected}
            >
              <FontAwesomeIcon icon={faDownload} className="reminder-cal-icon" />
              {calendarState.downloadedIcs ? t('onboarding_calendar_added', 'Added') + ' \u2713' : '.ics'}
            </button>
          </div>

          {/* Save button */}
          <button type="button" className="reminder-save-btn" onClick={handleSave}>
            {t('save_reminders', 'Save reminders')}
          </button>
        </>
      )}
    </div>
  );
}

export default ReminderSetupCard;
